/**
 * Painel de Lista de Indicações do SAPL Map (Retângulo Verde).
 *
 * Módulo independente posicionado no canto superior esquerdo.
 * Todo o cabeçalho é clicável para expandir/recolher o painel (sem botão X).
 *
 * @module components/IndicacoesPanel
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useIndicacoes, useSelectedIndicacao, usePins, useStats } from "../hooks/useIndicacoes";
import { useFilters } from "../hooks/useFilters";
import { canonicalizeBairro } from "../constants/neighborhoods";
import {
  THEMATIC_CATEGORIES,
  THEMATIC_CATEGORIES_ORDER,
  getCategoryStyle,
} from "../constants/categories";

import {
  CategoryFilterDropdown,
  YearsFilterDropdown,
  AuthorFilterDropdown,
  BairroFilterDropdown,
} from "./ChartFilterDropdowns";

/**
 * Skeleton para estado de carregamento
 */
function IndicacoesSkeleton() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            padding: "11px 13px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-color)",
            background: "var(--bg-surface)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="skeleton" style={{ width: 110, height: 18, borderRadius: "var(--radius-sm)" }} />
            <div className="skeleton" style={{ width: 70, height: 16, borderRadius: "var(--radius-sm)" }} />
          </div>
          <div className="skeleton" style={{ width: "90%", height: 18, borderRadius: 4 }} />
          <div className="skeleton" style={{ width: "60%", height: 14, borderRadius: 4 }} />
          <div style={{ display: "flex", gap: 6 }}>
            <div className="skeleton" style={{ width: 100, height: 18, borderRadius: "var(--radius-full)" }} />
            <div className="skeleton" style={{ width: 60, height: 18, borderRadius: "var(--radius-full)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Extrai a lista de autores de uma indicação (compatível com array futuro `ind.autores` ou string `ind.autor`).
 */
export function parseAutores(indOrStr) {
  if (indOrStr && typeof indOrStr === "object" && Array.isArray(indOrStr.autores)) {
    return indOrStr.autores.map((a) => String(a).trim()).filter(Boolean);
  }
  const raw = typeof indOrStr === "string" ? indOrStr : indOrStr?.autor || "";
  if (!raw || raw === "N/A") return [];
  return raw
    .split(",")
    .map((a) => a.trim())
    .filter(Boolean);
}

/**
 * Formata a exibição de autoria no card recolhido sem privilegiar um autor sobre os demais:
 * - Se todos os nomes couberem na linha (<= maxInlineChars), exibe todos os nomes separados por vírgula.
 * - Caso contrário, exibe "Múltiplos autores (N)" em texto simples (sem pílula).
 */
export function formatAuthorsCollapsed(indOrStr, maxInlineChars = 36) {
  const autores = parseAutores(indOrStr);
  if (autores.length === 0) {
    return {
      label: "Autoria não informada",
      countSuffix: null,
      isMulti: false,
      allAuthors: [],
      fullTooltip: "Autoria não informada",
    };
  }
  if (autores.length === 1) {
    return {
      label: autores[0],
      countSuffix: null,
      isMulti: false,
      allAuthors: autores,
      fullTooltip: autores[0],
    };
  }
  const joined = autores.join(", ");
  if (joined.length <= maxInlineChars) {
    return {
      label: joined,
      countSuffix: null,
      isMulti: true,
      allAuthors: autores,
      fullTooltip: `Autores: ${joined}`,
    };
  }
  return {
    label: "Múltiplos autores",
    countSuffix: `(${autores.length})`,
    isMulti: true,
    allAuthors: autores,
    fullTooltip: `Autores (${autores.length}): ${joined}`,
  };
}

/**
 * Resume a ementa removendo prefixos legislativos repetitivos ("Solicita...", "Requer...", "Indica...")
 * para uso como subtítulo de 1 linha (cards com endereço) ou título de até 2 linhas (cards sem endereço).
 */
export function formatEmentaSummary(ementa) {
  if (!ementa || !ementa.trim()) return "Texto da ementa não informado.";
  const cleaned = ementa
    .trim()
    .replace(
      /^(solicita(-se)?|requer(-se)?|indica(-se)?)\s+(ao\s+executivo\s+|à\s+prefeitura\s+)?(a\s+|o\s+|que\s+seja\s+feita\s+a\s+|que\s+seja\s+feito\s+o\s+|que\s+)?/i,
      ""
    )
    .trim();
  if (!cleaned) return ementa.trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Identifica se a indicação possui geometria especial ("area" ou "line").
 */
export function getGeometryKind(ind) {
  const geomType =
    ind?.geo_pins?.find(
      (p) => p.geometry_type && p.geometry_type !== "Point" && p.geometry_type !== "MultiPoint"
    )?.geometry_type ||
    ind?.enderecos_extraidos?.find(
      (e) =>
        e.geo_pin?.geometry_type &&
        e.geo_pin?.geometry_type !== "Point" &&
        e.geo_pin?.geometry_type !== "MultiPoint"
    )?.geo_pin?.geometry_type;
  if (!geomType) return null;
  if (geomType === "Polygon" || geomType === "MultiPolygon") return "area";
  return "line";
}

/**
 * Badge unificado de localização e geometria da indicação (altura e tipografia padronizadas via .card-pill)
 */
export function PublicBadge({ hasPin, isApproximate, addressKind, geomKind }) {
  let baseLabel;
  let bg;
  let fg;
  let border;

  if (!hasPin) {
    if (addressKind === "bairro") {
      baseLabel = "Aproximado";
      bg = "var(--status-approx-bg)";
      fg = "var(--status-approx-fg)";
      border = "var(--status-approx-border)";
    } else if (addressKind === "unlocated") {
      baseLabel = "Fora do mapa";
      bg = "var(--status-none-bg)";
      fg = "var(--status-none-fg)";
      border = "var(--status-none-border)";
    } else {
      baseLabel = "Sem local no mapa";
      bg = "var(--status-none-bg)";
      fg = "var(--status-none-fg)";
      border = "var(--status-none-border)";
    }
  } else if (isApproximate) {
    baseLabel = "Aproximado";
    bg = "var(--status-approx-bg)";
    fg = "var(--status-approx-fg)";
    border = "var(--status-approx-border)";
  } else {
    baseLabel = "Local exato";
    bg = "var(--status-exact-bg)";
    fg = "var(--status-exact-fg)";
    border = "var(--status-exact-border)";
  }

  const geomSuffix =
    hasPin && geomKind === "area"
      ? " · Área"
      : hasPin && geomKind === "line"
      ? " · Traçado"
      : "";

  const label = `${baseLabel}${geomSuffix}`;
  const title =
    geomKind === "area"
      ? `${baseLabel} — delimitado por área poligonal`
      : geomKind === "line"
      ? `${baseLabel} — delimitado por traçado viário`
      : label;

  return (
    <span
      className="pill-badge card-pill"
      style={{
        background: bg,
        color: fg,
        border: `1px solid ${border}`,
      }}
      title={title}
    >
      {label}
    </span>
  );
}

/**
 * Extrai e classifica o logradouro principal da indicação em 4 estados:
 * - "exact" | "approx": possui pin geocodificado no mapa
 * - "unlocated": endereço extraído do texto, mas sem coordenadas no mapa
 * - "bairro": apenas bairro informado
 * - "none": sem endereço nem bairro (abrangência geral; usa a ementa como título)
 */
export function getPrimaryAddress(ind) {
  const allPins = [
    ...(ind?.geo_pins || []),
    ...(ind?.enderecos_extraidos || []).map((e) => e.geo_pin).filter(Boolean),
  ];
  const geocodedPin = allPins.find(
    (p) => typeof p.latitude === "number" && typeof p.longitude === "number"
  );
  const pinWithAddress = allPins.find((p) => p.normalized_address || p.raw_address);

  // Consolidação resiliente do bairro da indicação
  const rawBairro =
    ind?.bairro ||
    geocodedPin?.bairro ||
    pinWithAddress?.bairro ||
    ind?.enderecos_extraidos?.find((e) => e.bairro)?.bairro;
  const cleanBairro =
    rawBairro && rawBairro !== "Não informado" && rawBairro.trim()
      ? canonicalizeBairro(rawBairro.trim().replace(/^Bairro\s+/i, ""))
      : null;

  if (geocodedPin && (geocodedPin.normalized_address || geocodedPin.raw_address || pinWithAddress)) {
    const raw =
      geocodedPin.normalized_address ||
      geocodedPin.raw_address ||
      pinWithAddress.normalized_address ||
      pinWithAddress.raw_address;
    const cleaned = raw.replace(/,?\s*Artur Nogueira\s*-\s*SP/i, "").trim();
    const isApprox = Boolean(geocodedPin.is_approximate);
    return {
      kind: isApprox ? "approx" : "exact",
      street: cleaned,
      bairro: cleanBairro,
      isApproximate: isApprox,
      hasCoords: true,
      extraCount: Math.max(0, (ind?.geo_pins?.length || 0) - 1),
    };
  }

  const extracted = ind?.enderecos_extraidos?.[0];
  if (extracted?.raw_text || pinWithAddress) {
    const raw = extracted?.raw_text || pinWithAddress.normalized_address || pinWithAddress.raw_address;
    const cleaned = raw.replace(/,?\s*Artur Nogueira\s*-\s*SP/i, "").trim();
    return {
      kind: "unlocated",
      street: cleaned,
      bairro: cleanBairro,
      isApproximate: true,
      hasCoords: Boolean(geocodedPin),
      extraCount: Math.max(0, (ind?.enderecos_extraidos?.length || 0) - 1),
    };
  }

  if (cleanBairro) {
    return {
      kind: "bairro",
      street: `${cleanBairro} · bairro`,
      bairro: cleanBairro,
      isApproximate: true,
      hasCoords: Boolean(geocodedPin),
      extraCount: 0,
    };
  }

  return {
    kind: "none",
    street: null,
    bairro: null,
    isApproximate: false,
    hasCoords: false,
    extraCount: 0,
  };
}

export function ThemeBadge({ thematicCategory }) {
  if (!thematicCategory) return null;

  const style = getCategoryStyle(thematicCategory);

  return (
    <span
      className="pill-badge card-pill card-pill--icon"
      style={{
        background: `${style.bgColor}14`,
        color: style.bgColor,
        border: `1px solid ${style.bgColor}33`,
      }}
    >
      <div
        className="card-pill__icon"
        style={{
          backgroundColor: style.solid || style.bgColor,
          color: style.onSolid || "#ffffff",
        }}
      >
        <span
          className="geom-svg-icon"
          style={{
            width: 9,
            height: 9,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          dangerouslySetInnerHTML={{ __html: style.icon }}
        />
      </div>
      <span>{style.label}</span>
    </span>
  );
}

/**
 * Componente principal do Painel de Indicações.
 *
 * @param {{
 *   isMobile?: boolean,
 *   isCollapsed?: boolean,
 *   onToggleCollapse?: () => void
 *   topOffset?: number
 * }} props
 */
export default function IndicacoesPanel({
  isMobile = false,
  isCollapsed = false,
  onToggleCollapse,
  topOffset = 68,
}) {
  const { indicacoes, total, page, pages, loading, goToPage } = useIndicacoes();
  const { selected, select, clear, isGeometryVisible, toggleGeometryVisible, triggerFocusMap } = useSelectedIndicacao();
  const { filters, applyFilters, resetFilters, hasActiveFilters } = useFilters();
  const { stats } = useStats();
  const { pins } = usePins();

  // Consolidação de autores disponíveis
  const availableAuthors = useMemo(() => {
    if (Array.isArray(stats?.autores) && stats.autores.length > 0) {
      return stats.autores;
    }
    const aSet = new Set();
    (pins || []).forEach((p) => {
      const a = p.indicacao?.autor || p.autor;
      if (a) {
        a.split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .forEach((name) => aSet.add(name));
      }
    });
    return Array.from(aSet).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [stats?.autores, pins]);

  // Consolidação de bairros disponíveis com canonicalização e unificação de apelidos
  const availableBairros = useMemo(() => {
    const bMap = new Map();
    if (Array.isArray(stats?.bairros) && stats.bairros.length > 0) {
      stats.bairros.forEach((b) => {
        const c = canonicalizeBairro(b);
        if (c && c !== "Não informado" && c.trim()) {
          bMap.set(c.toLowerCase(), c);
        }
      });
      return Array.from(bMap.values()).sort((a, b) => a.localeCompare(b, "pt-BR"));
    }
    (pins || []).forEach((p) => {
      const b =
        p.indicacao?.bairro ||
        p.bairro ||
        p.indicacao?.enderecos_extraidos?.[0]?.bairro;
      if (b && b !== "Não informado" && b.trim()) {
        const c = canonicalizeBairro(b.trim());
        if (c) bMap.set(c.toLowerCase(), c);
      }
    });
    (indicacoes || []).forEach((i) => {
      const b = i.bairro || i.enderecos_extraidos?.[0]?.bairro;
      if (b && b !== "Não informado" && b.trim()) {
        const c = canonicalizeBairro(b.trim());
        if (c) bMap.set(c.toLowerCase(), c);
      }
    });
    if (bMap.size === 0) {
      [
        "Bairro São Bento",
        "Bairrinho",
        "Centro",
        "Jardim Bela Vista",
        "Jardim Blumenau",
        "Jardim Conservani",
        "Jardim Faveri",
        "Jardim Planalto",
        "Jardim Sacilotto I",
        "Jardim Sacilotto II",
        "Parque das Flores",
        "Pq. Res. Itamaraty",
      ].forEach((b) => {
        const c = canonicalizeBairro(b);
        bMap.set(c.toLowerCase(), c);
      });
    }
    return Array.from(bMap.values()).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [stats?.bairros, pins, indicacoes]);

  // Consolidação de anos disponíveis
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    if (Array.isArray(stats?.anos)) {
      stats.anos.forEach((y) => {
        if (Number(y)) yearSet.add(Number(y));
      });
    }
    if (Array.isArray(stats?.demandas_por_ano)) {
      stats.demandas_por_ano.forEach((item) => {
        if (Number(item?.ano)) yearSet.add(Number(item.ano));
      });
    }
    (pins || []).forEach((p) => {
      const y = p.indicacao?.ano || p.ano;
      if (Number(y)) yearSet.add(Number(y));
    });
    if (yearSet.size === 0) {
      return [2026, 2025, 2024, 2023, 2022, 2021];
    }
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [stats?.anos, stats?.demandas_por_ano, pins]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filters.search || "");
  const [copiedCardId, setCopiedCardId] = useState(null);
  const [focusedCardId, setFocusedCardId] = useState(null);
  const [openedSaplCardId, setOpenedSaplCardId] = useState(null);

  const selectedCardRef = useRef(null);

  // Auto-scroll suave até o card selecionado quando uma indicação é ativada
  useEffect(() => {
    if (selected?.id && selectedCardRef.current) {
      selectedCardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selected?.id]);

  const handleCopyLink = (e, indId) => {
    e.stopPropagation();
    const url = new URL(window.location.href);
    url.searchParams.set("indicacao", String(indId));
    url.searchParams.delete("ind");
    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopiedCardId(indId);
      setTimeout(() => setCopiedCardId(null), 2000);
    });
  };

  useEffect(() => {
    setSearchTerm(filters.search || "");
  }, [filters.search]);

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    applyFilters({ search: searchTerm.trim() || null });
  };

  const toggleAno = (anoNum) => {
    let currentAnos = Array.isArray(filters.anos) ? [...filters.anos] : [];
    if (currentAnos.includes(anoNum)) {
      currentAnos = currentAnos.filter((y) => y !== anoNum);
    } else {
      currentAnos.push(anoNum);
    }
    applyFilters({
      anos: currentAnos,
    });
  };

  const toggleThematicCategory = (catKey) => {
    applyFilters({
      thematic_category: filters.thematic_category === catKey ? null : catKey,
    });
  };

  const toggleAproximado = (val) => {
    applyFilters({
      aproximado: filters.aproximado === val ? null : val,
    });
  };

  const activeFiltersCount = [
    filters.search,
    Array.isArray(filters.anos) && filters.anos.length > 0,
    filters.thematic_category,
    filters.autor,
    filters.bairro,
    filters.aproximado !== null && filters.aproximado !== undefined,
  ].filter(Boolean).length;

  // ── ESTADO RECOLHIDO: Botão flutuante minimalista no topo esquerdo ──
  if (isCollapsed) {
    if (isMobile) return null;
    return (
      <button
        type="button"
        className="glass-card"
        onClick={onToggleCollapse}
        style={{
          position: "absolute",
          top: topOffset,
          left: 14,
          zIndex: 1030,
          height: 38,
          padding: "0 14px",
          fontSize: "var(--text-label)",
          fontWeight: 600,
          color: "var(--text-primary)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-md)",
          boxShadow: "var(--shadow-md)",
          background: "var(--bg-glass)",
          transition: "all var(--transition-fast)",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
        }}
        title="Abrir Lista de Indicações"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--accent-primary)", flexShrink: 0 }}
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
        <span>Lista de Indicações</span>
        <span
          style={{
            background: "var(--bg-surface-elevated)",
            color: "var(--text-muted)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-full)",
            padding: "1px 6px",
            fontSize: "var(--text-micro)",
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {total}
        </span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: "var(--text-muted)", flexShrink: 0, marginLeft: 1 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
    );
  }

  const renderIndicationCard = (activeData, isCurrentSelected, isPinned = false) => {
    const primaryAddr = getPrimaryAddress(activeData);
    const geomKind = getGeometryKind(activeData);
    const authorInfo = formatAuthorsCollapsed(activeData);
    const ementaSummary = formatEmentaSummary(activeData.ementa);
    const dataFormatada =
      activeData.data_apresentacao ||
      (activeData.data ? new Date(activeData.data).toLocaleDateString("pt-BR") : activeData.ano);
    const hasCoords = primaryAddr.hasCoords;
    const isNoAddress = primaryAddr.kind === "none";

    const toggleCard = () => (isCurrentSelected ? clear() : select(activeData.id));

    return (
      <div
        key={activeData.id}
        ref={isCurrentSelected ? selectedCardRef : null}
        className={`ind-card ${isCurrentSelected ? "ind-card--selected" : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: isCurrentSelected ? 9 : 7,
          padding: "11px 13px",
        }}
      >
        {/* Cabeçalho do Card (Sempre visível e acessível por teclado para alternar acordeão) */}
        <div
          role="button"
          tabIndex={0}
          aria-expanded={isCurrentSelected}
          onClick={toggleCard}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleCard();
            }
          }}
          style={{
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            gap: 7,
            outline: "none",
          }}
          title={isCurrentSelected ? "Recolher detalhes" : `Expandir indicação nº ${activeData.numero}/${activeData.ano}`}
        >
          {/* Linha 1: Indicação nº à esquerda | Data e Chevron à direita */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span
              className="card-id-badge"
              style={{
                color: isCurrentSelected ? "var(--accent-primary)" : "var(--text-primary)",
                background: isCurrentSelected ? "var(--bg-surface)" : "var(--bg-surface-elevated)",
                border: "1px solid var(--border-color)",
              }}
            >
              Indicação nº {activeData.numero}/{activeData.ano}
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              {dataFormatada && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "var(--text-muted)",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dataFormatada}
                </span>
              )}
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: isCurrentSelected ? "var(--accent-primary)" : "var(--text-muted)",
                  transform: isCurrentSelected ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform var(--transition-fast)",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
          </div>

          {/* Linha 2: Endereço (apenas nos Estados A, B e C) */}
          {!isNoAddress && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
              <div
                style={{
                  marginTop: 2,
                  color: isCurrentSelected
                    ? "var(--accent-primary)"
                    : primaryAddr.kind === "unlocated"
                    ? "var(--text-muted)"
                    : primaryAddr.kind === "bairro"
                    ? "var(--status-approx-fg)"
                    : "var(--brand-navy)",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {primaryAddr.kind === "bairro" ? (
                  /* Ícone de região/bairro */
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
                    <line x1="9" y1="3" x2="9" y2="18" />
                    <line x1="15" y1="6" x2="15" y2="21" />
                  </svg>
                ) : primaryAddr.kind === "unlocated" ? (
                  /* Ícone de pin tracejado/fora do mapa */
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" strokeDasharray="3 2" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                ) : (
                  /* Ícone de pin padrão */
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
                <div
                  className="topo-address-plate"
                  style={{
                    fontSize: "var(--text-body-sm)",
                    fontWeight: 600,
                    color:
                      primaryAddr.kind === "unlocated"
                        ? "var(--text-secondary)"
                        : "var(--text-primary)",
                    lineHeight: "var(--leading-snug)",
                    wordBreak: "break-word",
                  }}
                >
                  <span>{primaryAddr.street}</span>
                  {primaryAddr.bairro && primaryAddr.kind !== "bairro" && (
                    <span
                      style={{
                        fontWeight: 500,
                        color: "var(--text-secondary)",
                        fontSize: "var(--text-label)",
                        marginLeft: 5,
                      }}
                      title={`Bairro: ${primaryAddr.bairro}`}
                    >
                      · {primaryAddr.bairro}
                    </span>
                  )}
                </div>

                {primaryAddr.extraCount > 0 && !isCurrentSelected && (
                  <span
                    style={{
                      fontSize: "var(--text-micro)",
                      color: "var(--text-muted)",
                      fontWeight: 500,
                      lineHeight: "var(--leading-snug)",
                    }}
                  >
                    +{primaryAddr.extraCount} outro{primaryAddr.extraCount > 1 ? "s" : ""} local
                    {primaryAddr.extraCount > 1 ? "is" : ""}
                  </span>
                )}

                {isCurrentSelected && activeData.enderecos_extraidos?.length > 1 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 1 }}>
                    {activeData.enderecos_extraidos.slice(1).map((extra, idx) => (
                      <div
                        key={extra.id || idx}
                        style={{
                          fontSize: "var(--text-label)",
                          color: "var(--text-secondary)",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 5,
                        }}
                      >
                        <span style={{ color: "var(--text-muted)", fontSize: "var(--text-micro)", marginTop: 2 }}>•</span>
                        <span className="topo-address-plate" style={{ fontSize: "var(--text-label)" }}>
                          {extra.raw_text}
                          {extra.bairro ? ` (${extra.bairro})` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Linha de Ementa: com ícone 📄 sempre consistente e expansão no próprio lugar */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: isNoAddress ? 7 : 6, minWidth: 0 }}>
            <svg
              width={isNoAddress ? "13" : "12"}
              height={isNoAddress ? "13" : "12"}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                marginTop: isNoAddress ? 2 : isCurrentSelected ? 3 : 2,
                color: isNoAddress
                  ? isCurrentSelected
                    ? "var(--accent-primary)"
                    : "var(--text-secondary)"
                  : "var(--text-muted)",
                flexShrink: 0,
              }}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>

            {isNoAddress ? (
              <div
                className="topo-address-plate"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "var(--text-body-sm)",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  lineHeight: "var(--leading-snug)",
                  wordBreak: "break-word",
                  ...(isCurrentSelected
                    ? {}
                    : {
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }),
                }}
              >
                {isCurrentSelected
                  ? activeData.ementa || ementaSummary
                  : ementaSummary}
              </div>
            ) : isCurrentSelected ? (
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "var(--text-body-sm)",
                  fontWeight: 400,
                  lineHeight: "var(--leading-normal)",
                  color: "var(--text-secondary)",
                  wordBreak: "break-word",
                }}
              >
                {activeData.ementa || "Texto da ementa não informado."}
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "var(--text-label)",
                  color: "var(--text-muted)",
                  fontWeight: 400,
                  lineHeight: "var(--leading-snug)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={activeData.ementa}
              >
                {ementaSummary}
              </div>
            )}
          </div>

          {/* Linha de Autoria: expande no próprio lugar ao selecionar, sem duplicar seção embaixo */}
          <div
            style={{
              display: "flex",
              alignItems: isCurrentSelected && authorInfo.isMulti ? "flex-start" : "center",
              gap: 6,
              minWidth: 0,
            }}
            title={!isCurrentSelected ? authorInfo.fullTooltip : undefined}
          >
            {authorInfo.isMulti ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  color: "var(--text-muted)",
                  flexShrink: 0,
                  marginTop: isCurrentSelected ? 2 : 0,
                }}
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: "var(--text-muted)", flexShrink: 0 }}
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}

            {isCurrentSelected && authorInfo.allAuthors.length > 0 ? (
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "var(--text-label)",
                  lineHeight: "var(--leading-snug)",
                  color: "var(--text-secondary)",
                  fontWeight: 500,
                  wordBreak: "break-word",
                }}
              >
                {authorInfo.allAuthors.map((autorNome, idx) => {
                  const isFilteredAuthor = filters.autor === autorNome;
                  return (
                    <span key={autorNome}>
                      <button
                        type="button"
                        className={`author-inline-btn ${isFilteredAuthor ? "author-inline-btn--active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          applyFilters({
                            autor: isFilteredAuthor ? null : autorNome,
                          });
                        }}
                        title={
                          isFilteredAuthor
                            ? `Remover filtro de ${autorNome}`
                            : `Filtrar indicações de ${autorNome}`
                        }
                      >
                        {autorNome}
                      </button>
                      {idx < authorInfo.allAuthors.length - 1 ? ", " : ""}
                    </span>
                  );
                })}
              </div>
            ) : (
              <span
                style={{
                  fontSize: "var(--text-label)",
                  color: "var(--text-secondary)",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {authorInfo.label}
                {authorInfo.countSuffix && (
                  <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>
                    {" "}
                    {authorInfo.countSuffix}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Linha de Pílulas: Categoria e Localização Unificada (altura única 22px) */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 1 }}>
            <ThemeBadge thematicCategory={activeData.thematic_category} />
            <PublicBadge
              hasPin={hasCoords}
              isApproximate={primaryAddr.isApproximate}
              addressKind={primaryAddr.kind}
              geomKind={geomKind}
            />
          </div>
        </div>

        {/* RODAPÉ EXPANDIDO (Apenas a barra de ações rápidas, sem duplicar Ementa ou Autoria) */}
        {isCurrentSelected && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              cursor: "default",
            }}
          >
            {/* Barra de Ações Rápidas (altura 26px e tipografia Inter padronizadas via .card-action-btn) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "nowrap",
                paddingTop: 8,
                borderTop: "1px solid var(--border-subtle)",
              }}
            >
              {/* Botão de Foco / Visualização no Mapa */}
              {hasCoords ? (() => {
                const hasAreaOrLine = Boolean(geomKind);
                const isPoly = geomKind === "area";
                const isAreaActive = Boolean(isGeometryVisible && hasAreaOrLine && isCurrentSelected);
                const isFocusedRecently = focusedCardId === activeData.id;

                let btnLabel = "Ver no mapa";
                let btnTitle = "Centralizar câmera no mapa";

                if (hasAreaOrLine) {
                  if (isAreaActive) {
                    btnLabel = isPoly ? "Ocultar área" : "Ocultar traçado";
                    btnTitle = isPoly ? "Ocultar área delimitada do mapa" : "Ocultar traçado viário do mapa";
                  } else {
                    btnLabel = isPoly ? "Ver área" : "Ver traçado";
                    btnTitle = isPoly ? "Exibir e focar área no mapa" : "Exibir e focar traçado no mapa";
                  }
                } else if (isFocusedRecently) {
                  btnLabel = "Focado!";
                  btnTitle = "Câmera centralizada na indicação";
                }

                const bg = isAreaActive
                  ? "rgba(12, 114, 240, 0.08)"
                  : isFocusedRecently
                  ? "var(--status-success-bg)"
                  : "var(--bg-surface)";

                const fg = isAreaActive
                  ? "var(--accent-primary)"
                  : isFocusedRecently
                  ? "var(--status-success-fg)"
                  : "var(--text-secondary)";

                const border = isAreaActive
                  ? "1px solid rgba(12, 114, 240, 0.35)"
                  : isFocusedRecently
                  ? "1px solid var(--status-success-border)"
                  : "1px solid var(--border-color)";

                return (
                  <button
                    type="button"
                    className="card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAreaActive) {
                        toggleGeometryVisible(false);
                      } else {
                        triggerFocusMap();
                        if (!hasAreaOrLine) {
                          setFocusedCardId(activeData.id);
                          setTimeout(() => setFocusedCardId(null), 1800);
                        }
                        if (isMobile && typeof onToggleCollapse === "function") {
                          onToggleCollapse();
                        }
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (!isAreaActive && !isFocusedRecently) {
                        e.currentTarget.style.borderColor = "var(--text-muted)";
                        e.currentTarget.style.color = "var(--text-primary)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAreaActive && !isFocusedRecently) {
                        e.currentTarget.style.borderColor = "var(--border-color)";
                        e.currentTarget.style.color = "var(--text-secondary)";
                      }
                    }}
                    style={{
                      background: bg,
                      border: border,
                      color: fg,
                      cursor: "pointer",
                    }}
                    title={btnTitle}
                  >
                    {isFocusedRecently ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : isAreaActive ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="2" x2="5" y1="12" y2="12" />
                        <line x1="19" x2="22" y1="12" y2="12" />
                        <line x1="12" x2="12" y1="2" y2="5" />
                        <line x1="12" x2="12" y1="19" y2="22" />
                        <circle cx="12" cy="12" r="7" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                    <span>{btnLabel}</span>
                  </button>
                );
              })() : (
                <span
                  className="card-action-btn"
                  style={{
                    background: "var(--bg-surface-elevated)",
                    border: "1px solid var(--border-color)",
                    color: "var(--text-muted)",
                    cursor: "default",
                  }}
                  title="Indicação sem coordenadas georreferenciadas"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" strokeDasharray="3 2" />
                    <circle cx="12" cy="10" r="2.5" />
                  </svg>
                  <span>Sem mapa</span>
                </span>
              )}

              {/* Botão Portal SAPL */}
              {activeData.sapl_id && (() => {
                const isOpenedRecently = openedSaplCardId === activeData.id;
                return (
                  <a
                    href={`https://sapl.arturnogueira.sp.leg.br/materia/${activeData.sapl_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenedSaplCardId(activeData.id);
                      setTimeout(() => setOpenedSaplCardId(null), 1800);
                    }}
                    onMouseEnter={(e) => {
                      if (!isOpenedRecently) {
                        e.currentTarget.style.borderColor = "var(--text-muted)";
                        e.currentTarget.style.color = "var(--text-primary)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isOpenedRecently) {
                        e.currentTarget.style.borderColor = "var(--border-color)";
                        e.currentTarget.style.color = "var(--text-secondary)";
                      }
                    }}
                    style={{
                      background: isOpenedRecently ? "rgba(12, 114, 240, 0.10)" : "var(--bg-surface)",
                      border: isOpenedRecently ? "1px solid rgba(12, 114, 240, 0.35)" : "1px solid var(--border-color)",
                      color: isOpenedRecently ? "var(--accent-primary)" : "var(--text-secondary)",
                      transform: isOpenedRecently ? "scale(0.97)" : "scale(1)",
                    }}
                    title={isOpenedRecently ? "Carregando matéria no Portal SAPL..." : "Abrir matéria oficial no SAPL da Câmara"}
                  >
                    {isOpenedRecently ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                    )}
                    <span>{isOpenedRecently ? "Abrindo..." : "Portal SAPL"}</span>
                  </a>
                );
              })()}

              {/* Botão Copiar Link */}
              <button
                type="button"
                className="card-action-btn"
                onClick={(e) => handleCopyLink(e, activeData.id)}
                onMouseEnter={(e) => {
                  if (copiedCardId !== activeData.id) {
                    e.currentTarget.style.borderColor = "var(--text-muted)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (copiedCardId !== activeData.id) {
                    e.currentTarget.style.borderColor = "var(--border-color)";
                    e.currentTarget.style.color = "var(--text-secondary)";
                  }
                }}
                style={{
                  background: copiedCardId === activeData.id ? "var(--status-success-bg)" : "var(--bg-surface)",
                  color: copiedCardId === activeData.id ? "var(--status-success-fg)" : "var(--text-secondary)",
                  border: copiedCardId === activeData.id ? "1px solid var(--status-success-border)" : "1px solid var(--border-color)",
                  cursor: "pointer",
                }}
                title="Copiar link direto para esta indicação"
              >
                {copiedCardId === activeData.id ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    <span>Copiar Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── ESTADO EXPANDIDO: Painel completo com cabeçalho clicável ──
  return (
    <div
      className="glass-card"
      onWheel={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        top: topOffset,
        bottom: isMobile ? 68 : 14,
        left: isMobile ? 12 : 14,
        right: isMobile ? 12 : "auto",
        width: isMobile ? "auto" : 380,
        height: isMobile ? "calc(100% - 82px)" : "calc(100% - 28px)",
        maxHeight: isMobile ? "calc(100% - 82px)" : "calc(100% - 28px)",
        boxSizing: "border-box",
        zIndex: 1030,
        display: "flex",
        flexDirection: "column",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-glass)",
        border: "1px solid var(--border-color)",
        overflow: "hidden",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
      }}
    >
      {/* Cabeçalho Clicável: clicar em todo o header recolhe o painel */}
      <div
        onClick={onToggleCollapse}
        style={{
          height: 46,
          minHeight: 46,
          maxHeight: 46,
          padding: "0 16px",
          background: "var(--bg-glass)",
          borderBottom: "1px solid var(--border-color)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
          boxSizing: "border-box",
          flexShrink: 0,
          transition: "background var(--transition-fast)",
        }}
        title="Clique para recolher"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "var(--accent-primary)", flexShrink: 0 }}
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 8 9" />
          </svg>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: "var(--text-body-sm)", fontWeight: 700, color: "var(--text-primary)" }}>
              Lista de Indicações
            </span>
            <span
              style={{
                background: "var(--bg-surface-elevated)",
                color: "var(--text-muted)",
                border: "1px solid var(--border-color)",
                borderRadius: "var(--radius-full)",
                padding: "1px 6px",
                fontSize: "var(--text-micro)",
                fontWeight: 700,
              }}
            >
              {total}
            </span>
          </div>
        </div>

        {/* Indicador Chevron de recolhimento */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: "var(--text-label)",
            color: "var(--text-muted)",
            fontWeight: 500,
          }}
        >
          <span>Recolher</span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </div>
      </div>

      {/* ── LISTA MESTRE DE INDICAÇÕES COM SCROLL E PAGINAÇÃO ── */}
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* Barra de Busca e Gatilho de Filtros Integrados */}
          <div
            style={{
              padding: "8px 12px",
              background: "var(--bg-glass)",
              borderBottom: "1px solid var(--border-color)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <form
              onSubmit={handleSearchSubmit}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <div style={{ position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ position: "absolute", left: 9, color: "var(--text-muted)", pointerEvents: "none" }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar rua, assunto, autor..."
                  style={{
                    width: "100%",
                    height: 32,
                    boxSizing: "border-box",
                    padding: "0 26px 0 28px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    fontSize: "var(--text-label)",
                    outline: "none",
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm("");
                      applyFilters({ search: null });
                    }}
                    style={{
                      position: "absolute",
                      right: 6,
                      background: "none",
                      border: "none",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Limpar busca"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Botão de Expansão dos Filtros Integrados */}
              <button
                type="button"
                onClick={() => setFiltersOpen(!filtersOpen)}
                style={{
                  height: 32,
                  boxSizing: "border-box",
                  padding: "0 9px",
                  borderRadius: "var(--radius-md)",
                  border: `1px solid ${filtersOpen || activeFiltersCount > 0 ? "var(--accent-primary)" : "var(--border-color)"}`,
                  background: filtersOpen || activeFiltersCount > 0 ? "var(--accent-light)" : "var(--bg-surface)",
                  color: filtersOpen || activeFiltersCount > 0 ? "var(--accent-primary)" : "var(--text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: "var(--text-label)",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all var(--transition-fast)",
                  whiteSpace: "nowrap",
                }}
                title={filtersOpen ? "Recolher filtros" : "Expandir filtros"}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Filtros</span>
                {activeFiltersCount > 0 && (
                  <span
                    style={{
                      background: "var(--accent-primary)",
                      color: "#ffffff",
                      borderRadius: "var(--radius-full)",
                      padding: "1px 5px",
                      fontSize: "var(--text-micro)",
                      fontWeight: 700,
                    }}
                  >
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </form>

            {/* Gaveta de Filtros Estruturada em Caixas (idêntica à página de Estatísticas) */}
            {filtersOpen && (
              <div
                style={{
                  padding: "10px 4px 6px 4px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  borderTop: "1px solid var(--border-subtle)",
                }}
              >
                {/* Grid 2x2 de Seletores em Caixa (estilo direto da página de Estatísticas) */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                    gap: 8,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  {/* 1. Categoria Temática */}
                  <div style={{ minWidth: 0 }}>
                    <CategoryFilterDropdown
                      selectedCategory={filters.thematic_category}
                      onChange={(catKey) => applyFilters({ thematic_category: catKey })}
                      fullWidth={true}
                      align="left"
                    />
                  </div>

                  {/* 2. Exercício / Ano */}
                  <div style={{ minWidth: 0 }}>
                    <YearsFilterDropdown
                      availableYears={availableYears}
                      selectedYears={Array.isArray(filters.anos) ? filters.anos : []}
                      onChange={(newYears) =>
                        applyFilters({
                          anos: newYears,
                        })
                      }
                      fullWidth={true}
                      align="right"
                    />
                  </div>

                  {/* 3. Parlamentar */}
                  <div style={{ minWidth: 0 }}>
                    <AuthorFilterDropdown
                      availableAuthors={availableAuthors}
                      selectedAuthor={filters.autor}
                      onChange={(authorVal) => applyFilters({ autor: authorVal })}
                      singleSelect={true}
                      fullWidth={true}
                      align="left"
                    />
                  </div>

                  {/* 4. Bairro */}
                  <div style={{ minWidth: 0 }}>
                    <BairroFilterDropdown
                      availableBairros={availableBairros}
                      selectedBairro={filters.bairro}
                      onChange={(bairroVal) => applyFilters({ bairro: bairroVal })}
                      fullWidth={true}
                      align="right"
                    />
                  </div>
                </div>

                {/* 5. Filtro de Precisão (Segmented Control Refinado em Caixa) */}
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--text-micro)",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "var(--tracking-wide)",
                        color: "var(--text-muted)",
                      }}
                    >
                      Precisão do Endereço
                    </span>
                    {filters.aproximado !== null && filters.aproximado !== undefined && (
                      <span
                        onClick={() => applyFilters({ aproximado: null })}
                        style={{
                          fontSize: "var(--text-micro)",
                          color: "var(--accent-primary)",
                          cursor: "pointer",
                          fontWeight: 700,
                          textDecoration: "underline",
                        }}
                      >
                        Limpar
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      background: "var(--bg-app)",
                      padding: 2,
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--border-color)",
                      height: 32,
                      boxSizing: "border-box",
                    }}
                  >
                    {[
                      { label: "Todos", val: null },
                      { label: "Exato", val: false },
                      { label: "Aproximado", val: true },
                    ].map((opt) => {
                      const isSelected = filters.aproximado === opt.val;
                      return (
                        <button
                          key={String(opt.val)}
                          type="button"
                          onClick={() => toggleAproximado(opt.val)}
                          style={{
                            height: "100%",
                            border: "none",
                            borderRadius: "var(--radius-xs)",
                            background: isSelected ? "var(--accent-primary)" : "transparent",
                            color: isSelected ? "#ffffff" : "var(--text-secondary)",
                            fontSize: "var(--text-micro)",
                            fontWeight: isSelected ? 700 : 600,
                            cursor: "pointer",
                            boxShadow: isSelected ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                            transition: "all var(--transition-fast)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 6. Ações de Rodapé: Limpar todos os filtros */}
                {hasActiveFilters && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: 8,
                      borderTop: "1px solid var(--border-subtle)",
                      marginTop: 2,
                    }}
                  >
                    <span style={{ fontSize: "var(--text-micro)", color: "var(--text-muted)", fontWeight: 600 }}>
                      {activeFiltersCount} {activeFiltersCount === 1 ? "filtro ativo" : "filtros ativos"}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        resetFilters();
                        setSearchTerm("");
                      }}
                      style={{
                        height: 28,
                        padding: "0 10px",
                        fontSize: "var(--text-micro)",
                        fontWeight: 700,
                        borderRadius: "var(--radius-sm)",
                        background: "transparent",
                        color: "var(--accent-primary)",
                        border: "1px solid var(--accent-primary)",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        transition: "all var(--transition-fast)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "var(--accent-light)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      <span>Limpar filtros</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              position: "relative",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
              padding: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {/* Indicação selecionada no mapa que está fora da página atual de resultados */}
            {selected && !indicacoes.some((i) => i.id === selected.id) && (
              <div style={{ marginBottom: 4 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: "var(--text-micro)",
                    fontWeight: 700,
                    color: "var(--accent-primary)",
                    letterSpacing: "var(--tracking-wide)",
                    textTransform: "uppercase",
                    marginBottom: 5,
                    padding: "0 2px",
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>Indicação selecionada no mapa</span>
                </div>
                {renderIndicationCard(selected, true, true)}
                <div style={{ height: 1, background: "var(--border-subtle)", margin: "8px 0" }} />
              </div>
            )}

            {loading ? (
              <IndicacoesSkeleton />
            ) : indicacoes.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: "var(--text-muted)", fontSize: "var(--text-body-sm)" }}>
                Nenhuma indicação encontrada para os filtros ativos.
              </div>
            ) : (
              indicacoes.map((ind) => {
                const isCurrentSelected = selected?.id === ind.id;
                const activeData = isCurrentSelected && selected ? selected : ind;
                return renderIndicationCard(activeData, isCurrentSelected);
              })
            )}
          </div>


          {/* Paginação Fixa no Rodapé com Salto Rápido de Página */}
          {pages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                borderTop: "1px solid var(--border-color)",
                background: "var(--bg-surface-elevated)",
                flexShrink: 0,
                gap: 6,
              }}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                style={{
                  padding: "5px 8px",
                  fontSize: "var(--text-label)",
                  fontWeight: 600,
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  opacity: page <= 1 ? 0.4 : 1,
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Ant.
              </button>

              {/* Seletor / Indicador de Página */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "var(--text-label)", color: "var(--text-muted)", fontWeight: 600 }}>
                <span>Pág</span>
                <input
                  type="number"
                  min="1"
                  max={pages}
                  value={page}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1 && val <= pages) {
                      goToPage(val);
                    }
                  }}
                  style={{
                    width: 44,
                    padding: "2px 4px",
                    textAlign: "center",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    fontSize: "var(--text-label)",
                    fontWeight: 700,
                    outline: "none",
                  }}
                />
                <span>de {pages}</span>
              </div>

              <button
                type="button"
                disabled={page >= pages}
                onClick={() => goToPage(page + 1)}
                style={{
                  padding: "5px 8px",
                  fontSize: "var(--text-label)",
                  fontWeight: 600,
                  cursor: page >= pages ? "not-allowed" : "pointer",
                  opacity: page >= pages ? 0.4 : 1,
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--radius-sm)",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                Próx.
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
    </div>
  );
}

